import React, { useState, useRef } from 'react';
import { User, Building2, Users, Briefcase, Edit2, Trash2, Plus, X, Save, ChevronDown, ChevronUp, Menu, Upload, Info, Check, CreditCard, Bell, Crown, Star, Camera, Copy } from 'lucide-react';
import { useNavigate, useOutletContext } from 'react-router-dom';
import { DashboardHeader } from '../components/dashboard/DashboardHeader';
interface DashboardContext {
  toggleMobileSidebar: () => void;
}
interface MyAccountProps {
  onMenuClick?: () => void;
  onNavigateToAICopilot?: () => void;
  onNavigate?: (path: string) => void;
}
interface Business {
  id: string;
  name: string;
  address: string;
  city: string;
  province: string;
  postalCode: string;
  hstNumber: string;
  registrationNumber: string;
  isDefault?: boolean;
}
interface FamilyMember {
  id: string;
  firstName: string;
  lastName: string;
  relationship: string;
  isDefault?: boolean;
}
interface Employee {
  id: string;
  firstName: string;
  lastName: string;
  position: string;
  email: string;
  phone?: string;
  isDefault?: boolean;
}
export function MyAccount({
  onMenuClick,
  onNavigateToAICopilot,
  onNavigate
}: MyAccountProps) {
  const navigate = useNavigate();
  const {
    toggleMobileSidebar
  } = useOutletContext<DashboardContext>() || {
    toggleMobileSidebar: () => {}
  };
  // Tab state - default to first tab
  const [activeTab, setActiveTab] = useState('basic-info');
  // Notification toggles
  const [emailNotifications, setEmailNotifications] = useState(true);
  const [inAppNotifications, setInAppNotifications] = useState(true);
  // Modal states
  const [showPersonalInfoModal, setShowPersonalInfoModal] = useState(false);
  const [showBusinessModal, setShowBusinessModal] = useState(false);
  const [showFamilyModal, setShowFamilyModal] = useState(false);
  const [showEmployeeModal, setShowEmployeeModal] = useState(false);
  const [editingBusiness, setEditingBusiness] = useState<Business | null>(null);
  const [editingFamily, setEditingFamily] = useState<FamilyMember | null>(null);
  const [editingEmployee, setEditingEmployee] = useState<Employee | null>(null);
  // Data states with Canadian addresses and Western names
  const [businesses, setBusinesses] = useState<Business[]>([{
    id: '1',
    name: 'Maple Leaf Restaurant',
    address: '123 Queen Street West',
    city: 'Toronto',
    province: 'Ontario',
    postalCode: 'M5H 2N2',
    hstNumber: '123456789RT0001',
    registrationNumber: 'ON-12345',
    isDefault: true
  }, {
    id: '2',
    name: 'Northern Lights Consulting',
    address: '456 Yonge Street',
    city: 'Toronto',
    province: 'Ontario',
    postalCode: 'M4Y 1X5',
    hstNumber: '987654321RT0002',
    registrationNumber: 'ON-67890',
    isDefault: false
  }, {
    id: '3',
    name: 'Pacific Coast Trading Co.',
    address: '789 Granville Street',
    city: 'Vancouver',
    province: 'British Columbia',
    postalCode: 'V6Z 1K3',
    hstNumber: '456789123RT0003',
    registrationNumber: 'BC-24680',
    isDefault: false
  }]);
  const [familyMembers, setFamilyMembers] = useState<FamilyMember[]>([{
    id: '1',
    firstName: 'Sarah',
    lastName: 'Johnson',
    relationship: 'Spouse',
    isDefault: true
  }]);
  const [employees, setEmployees] = useState<Employee[]>([{
    id: '1',
    firstName: 'Michael',
    lastName: 'Smith',
    position: 'Manager',
    email: 'michael.smith@example.com',
    phone: '+1 (416) 555-0123',
    isDefault: true
  }]);
  const [expandedBusinesses, setExpandedBusinesses] = useState<Set<string>>(new Set());
  const [expandedFamily, setExpandedFamily] = useState<Set<string>>(new Set());
  const [expandedEmployees, setExpandedEmployees] = useState<Set<string>>(new Set());
  const [profilePicture, setProfilePicture] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const handleProfilePictureUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file && (file.type === 'image/jpeg' || file.type === 'image/png')) {
      const reader = new FileReader();
      reader.onload = event => {
        setProfilePicture(event.target?.result as string);
      };
      reader.readAsDataURL(file);
    }
    // Reset file input so the same file can be re-selected
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };
  const handleProfilePictureClick = () => {
    if (!profilePicture) {
      fileInputRef.current?.click();
    }
  };
  const handleDeleteProfilePicture = (e: React.MouseEvent) => {
    e.stopPropagation();
    setProfilePicture(null);
  };
  const toggleBusinessExpand = (id: string) => {
    const newExpanded = new Set(expandedBusinesses);
    if (newExpanded.has(id)) {
      newExpanded.delete(id);
    } else {
      newExpanded.add(id);
    }
    setExpandedBusinesses(newExpanded);
  };
  const toggleFamilyExpand = (id: string) => {
    const newExpanded = new Set(expandedFamily);
    if (newExpanded.has(id)) {
      newExpanded.delete(id);
    } else {
      newExpanded.add(id);
    }
    setExpandedFamily(newExpanded);
  };
  const toggleEmployeeExpand = (id: string) => {
    const newExpanded = new Set(expandedEmployees);
    if (newExpanded.has(id)) {
      newExpanded.delete(id);
    } else {
      newExpanded.add(id);
    }
    setExpandedEmployees(newExpanded);
  };
  const handleAddBusiness = () => {
    setEditingBusiness(null);
    setShowBusinessModal(true);
  };
  const handleEditBusiness = (business: Business) => {
    setEditingBusiness(business);
    setShowBusinessModal(true);
  };
  const handleDeleteBusiness = (id: string) => {
    setBusinesses(businesses.filter(b => b.id !== id));
  };
  const handleAddFamily = () => {
    setEditingFamily(null);
    setShowFamilyModal(true);
  };
  const handleEditFamily = (member: FamilyMember) => {
    setEditingFamily(member);
    setShowFamilyModal(true);
  };
  const handleDeleteFamily = (id: string) => {
    setFamilyMembers(familyMembers.filter(f => f.id !== id));
  };
  const handleAddEmployee = () => {
    setEditingEmployee(null);
    setShowEmployeeModal(true);
  };
  const handleEditEmployee = (employee: Employee) => {
    setEditingEmployee(employee);
    setShowEmployeeModal(true);
  };
  const handleDeleteEmployee = (id: string) => {
    setEmployees(employees.filter(e => e.id !== id));
  };
  const handleSetDefaultBusiness = (id: string) => {
    setBusinesses(businesses.map(b => ({
      ...b,
      isDefault: b.id === id
    })));
  };
  const handleSetDefaultFamily = (id: string) => {
    setFamilyMembers(familyMembers.map(f => ({
      ...f,
      isDefault: f.id === id
    })));
  };
  const handleSetDefaultEmployee = (id: string) => {
    setEmployees(employees.map(e => ({
      ...e,
      isDefault: e.id === id
    })));
  };
  const canadianProvinces = ['Alberta', 'British Columbia', 'Manitoba', 'New Brunswick', 'Newfoundland and Labrador', 'Northwest Territories', 'Nova Scotia', 'Nunavut', 'Ontario', 'Prince Edward Island', 'Quebec', 'Saskatchewan', 'Yukon'];
  const handleNavigateToAICopilot = () => {
    if (onNavigateToAICopilot) {
      onNavigateToAICopilot();
    } else {
      navigate('/ai-copilot');
    }
  };
  const handleNavigate = (path: string) => {
    if (onNavigate) {
      onNavigate(path);
    } else {
      navigate(path);
    }
  };
  const handleMenuClick = () => {
    if (onMenuClick) {
      onMenuClick();
    } else {
      toggleMobileSidebar();
    }
  };
  return <div className="min-h-screen bg-aliceBlue" data-id="element-3313">
      {/* Replace custom header with DashboardHeader */}
      <DashboardHeader categories={[]} onMenuClick={handleMenuClick} onNavigateToAICopilot={handleNavigateToAICopilot} onNavigate={handleNavigate} data-id="element-3314" />

      {/* Main Content */}
      <main className="max-w-4xl mx-auto p-4 sm:p-6" data-id="element-3315">
        <div className="bg-white rounded-xl border border-borderColor shadow-sm overflow-hidden" data-id="element-3316">
          {/* Tab Navigation */}
          <div className="border-b border-borderColor bg-white" data-id="element-3317">
            <div className="flex items-center" data-id="element-3318">
              <div className="flex overflow-x-auto flex-1 min-w-0" data-id="element-3319">
                <button onClick={() => setActiveTab('basic-info')} className={`flex items-center gap-1.5 px-4 sm:px-6 py-3 sm:py-4 text-sm font-bold whitespace-nowrap transition-colors relative ${activeTab === 'basic-info' ? 'text-primary' : 'text-secondary hover:text-primary'}`} data-id="element-3320">
                  Basic Info
                  <Info className={`w-3.5 h-3.5 ${activeTab === 'basic-info' ? 'text-violetBlue' : 'text-violetBlue/50'}`} data-id="element-3321" />
                  {activeTab === 'basic-info' && <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-violetBlue" data-id="element-3322"></div>}
                </button>
                <button onClick={() => setActiveTab('notifications')} className={`flex items-center gap-1.5 px-4 sm:px-6 py-3 sm:py-4 text-sm font-bold whitespace-nowrap transition-colors relative ${activeTab === 'notifications' ? 'text-primary' : 'text-secondary hover:text-primary'}`} data-id="element-3323">
                  Notification Settings
                  <Info className={`w-3.5 h-3.5 ${activeTab === 'notifications' ? 'text-violetBlue' : 'text-violetBlue/50'}`} data-id="element-3324" />
                  {activeTab === 'notifications' && <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-violetBlue" data-id="element-3325"></div>}
                </button>
                <button onClick={() => setActiveTab('document-settings')} className={`flex items-center gap-1.5 px-4 sm:px-6 py-3 sm:py-4 text-sm font-bold whitespace-nowrap transition-colors relative ${activeTab === 'document-settings' ? 'text-primary' : 'text-secondary hover:text-primary'}`} data-id="element-3326">
                  Document Prefill Settings
                  <Info className={`w-3.5 h-3.5 ${activeTab === 'document-settings' ? 'text-violetBlue' : 'text-violetBlue/50'}`} data-id="element-3327" />
                  {activeTab === 'document-settings' && <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-violetBlue" data-id="element-3328"></div>}
                </button>
              </div>
              {/* User ID */}
              <div className="hidden sm:flex items-center gap-1.5 px-4 sm:px-6 flex-shrink-0" data-id="element-3329">
                <span className="text-xs whitespace-nowrap text-[#00d9ce]" data-id="element-3330">
                  User ID:{' '}
                  <span className="font-medium" data-id="element-3331">LV-DIGILITE-00045</span>
                </span>
                <button onClick={() => {
                navigator.clipboard.writeText('LV-DIGILITE-00045');
              }} className="p-1 text-secondary hover:text-violetBlue hover:bg-aliceBlue rounded transition-colors" title="Copy User ID" data-id="element-3332">
                  <Copy className="w-3.5 h-3.5" data-id="element-3333" />
                </button>
              </div>
            </div>
          </div>

          {/* Tab Content */}
          <div className="p-4 sm:p-6" data-id="element-3334">
            {/* Basic Info Tab - Redesigned */}
            {activeTab === 'basic-info' && <div data-id="element-3335">
                <div className="flex flex-col md:flex-row items-start gap-8 md:gap-12" data-id="element-3336">
                  {/* Left: Profile Picture Section */}
                  <div className="flex flex-col w-full md:w-1/3 flex-shrink-0 items-center justify-end" data-id="element-3337">
                    {/* Avatar */}
                    <div className="mb-4" data-id="element-3338">
                      <input ref={fileInputRef} type="file" accept="image/jpeg,image/png" onChange={handleProfilePictureUpload} className="hidden" data-id="element-3339" />
                      <div className="relative inline-block" data-id="element-3340">
                        <div onClick={handleProfilePictureClick} className={`w-32 h-32 rounded-full bg-aliceBlue border-2 border-borderColor flex items-center justify-center ${!profilePicture ? 'cursor-pointer group hover:border-violetBlue' : ''} transition-all relative overflow-hidden shadow-sm flex-shrink-0`} data-id="element-3341">
                          {profilePicture ? <img src={profilePicture} alt="Profile" className="w-full h-full object-cover" data-id="element-3342" /> : <Camera className="w-10 h-10 text-borderColor group-hover:text-violetBlue transition-colors" strokeWidth={1.5} data-id="element-3343" />}
                          {/* Hover overlay - only when no picture */}
                          {!profilePicture && <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center" data-id="element-3344">
                              <Camera className="w-8 h-8 text-white" data-id="element-3345" />
                            </div>}
                        </div>
                        {/* Delete badge - bottom-right of avatar */}
                        {profilePicture && <button onClick={handleDeleteProfilePicture} className="absolute bottom-0 right-0 w-9 h-9 flex items-center justify-center bg-white rounded-full shadow-md border border-borderColor hover:shadow-lg hover:scale-110 transition-all z-10" title="Remove profile picture" data-id="element-3346">
                            <img src="https://cdn.magicpatterns.com/uploads/iYca9pwV9E3BzuX8961P2W/Delete.svg" alt="Delete" className="w-5 h-5" data-id="element-3347" />
                          </button>}
                      </div>
                    </div>

                    {/* Help Note */}
                    <p className="text-sm text-secondary mb-4 leading-relaxed text-center" data-id="element-3348">
                      Add a profile picture (JPG or PNG) so lawyers know who
                      they'll be working with.
                    </p>

                    {/* Button */}
                  </div>

                  {/* Right: Form Fields */}
                  <div className="w-full md:w-2/3 grid grid-cols-1 sm:grid-cols-2 gap-[26px]" data-id="element-3349">
                    <div data-id="element-3350">
                      <label className="block text-sm font-medium text-secondary mb-2" data-id="element-3351">
                        <span className="text-secondary" data-id="element-3352">* </span>First Name
                      </label>
                      <input type="text" defaultValue="John" className="w-full px-4 py-3 border border-borderColor rounded text-sm text-primary focus:outline-none focus:ring-1 focus:ring-violetBlue focus:border-violetBlue" data-id="element-3353" />
                    </div>

                    <div data-id="element-3354">
                      <label className="block text-sm font-medium text-secondary mb-2" data-id="element-3355">
                        <span className="text-secondary" data-id="element-3356">* </span>Last Name
                      </label>
                      <input type="text" defaultValue="Due" className="w-full px-4 py-3 border border-borderColor rounded text-sm text-primary focus:outline-none focus:ring-1 focus:ring-violetBlue focus:border-violetBlue" data-id="element-3357" />
                    </div>

                    <div data-id="element-3358">
                      <label className="block text-sm font-medium text-secondary mb-2" data-id="element-3359">
                        Email
                      </label>
                      <input type="email" defaultValue="JohnDue@XXX.com*" disabled className="w-full px-4 py-3 border border-borderColor rounded text-sm bg-aliceBlue/50 text-secondary focus:outline-none" data-id="element-3360" />
                    </div>

                    <div data-id="element-3361">
                      <label className="block text-sm font-medium text-secondary mb-2" data-id="element-3362">
                        <span className="text-secondary" data-id="element-3363">* </span>Phone Number
                      </label>
                      <input type="tel" defaultValue="(111)222-3344" className="w-full px-4 py-3 border border-borderColor rounded text-sm text-primary focus:outline-none focus:ring-1 focus:ring-violetBlue focus:border-violetBlue" data-id="element-3364" />
                    </div>

                    <div data-id="element-3365">
                      <label className="block text-sm font-medium text-secondary mb-2" data-id="element-3366">
                        Contact Email
                      </label>
                      <input type="email" placeholder="" className="w-full px-4 py-3 border border-borderColor rounded text-sm text-primary focus:outline-none focus:ring-1 focus:ring-violetBlue focus:border-violetBlue" data-id="element-3367" />
                    </div>

                    <div data-id="element-3368">
                      <label className="block text-sm font-medium text-secondary mb-2" data-id="element-3369">
                        Contact Phone
                      </label>
                      <input type="tel" placeholder="" className="w-full px-4 py-3 border border-borderColor rounded text-sm text-primary focus:outline-none focus:ring-1 focus:ring-violetBlue focus:border-violetBlue" data-id="element-3370" />
                    </div>
                  </div>
                </div>
              </div>}

            {/* Billing Tab */}
            {activeTab === 'billing' && <div data-id="element-3371">
                <div className="mb-4" data-id="element-3372">
                  <h3 className="text-sm font-semibold text-primary mb-2" data-id="element-3373">
                    Billing Information
                  </h3>
                  <p className="text-xs text-secondary mb-4" data-id="element-3374">
                    Please provide your billing information
                  </p>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-3375">
                  <div data-id="element-3376">
                    <label className="block text-sm text-secondary mb-2" data-id="element-3377">
                      First Name
                    </label>
                    <input type="text" placeholder="First Name" className="w-full px-3 py-2 border border-borderColor rounded-lg focus:outline-none focus:ring-2 focus:ring-turquoise/20 focus:border-turquoise text-sm" data-id="element-3378" />
                  </div>
                  <div data-id="element-3379">
                    <label className="block text-sm text-secondary mb-2" data-id="element-3380">
                      Last Name
                    </label>
                    <input type="text" placeholder="Last Name" className="w-full px-3 py-2 border border-borderColor rounded-lg focus:outline-none focus:ring-2 focus:ring-turquoise/20 focus:border-turquoise text-sm" data-id="element-3381" />
                  </div>
                  <div data-id="element-3382">
                    <label className="block text-sm text-secondary mb-2" data-id="element-3383">
                      Phone Number
                    </label>
                    <input type="tel" placeholder="+1 (416) 555-0100" className="w-full px-3 py-2 border border-borderColor rounded-lg focus:outline-none focus:ring-2 focus:ring-turquoise/20 focus:border-turquoise text-sm" data-id="element-3384" />
                  </div>
                  <div data-id="element-3385">
                    <label className="block text-sm text-secondary mb-2" data-id="element-3386">
                      Postal Code
                    </label>
                    <input type="text" placeholder="M5H 2N2" className="w-full px-3 py-2 border border-borderColor rounded-lg focus:outline-none focus:ring-2 focus:ring-turquoise/20 focus:border-turquoise text-sm" data-id="element-3387" />
                  </div>
                  <div className="sm:col-span-2" data-id="element-3388">
                    <label className="block text-sm text-secondary mb-2" data-id="element-3389">
                      Address
                    </label>
                    <input type="text" placeholder="123 Queen Street West" className="w-full px-3 py-2 border border-borderColor rounded-lg focus:outline-none focus:ring-2 focus:ring-turquoise/20 focus:border-turquoise text-sm" data-id="element-3390" />
                  </div>
                  <div data-id="element-3391">
                    <label className="block text-sm text-secondary mb-2" data-id="element-3392">
                      City
                    </label>
                    <input type="text" placeholder="Toronto" className="w-full px-3 py-2 border border-borderColor rounded-lg focus:outline-none focus:ring-2 focus:ring-turquoise/20 focus:border-turquoise text-sm" data-id="element-3393" />
                  </div>
                  <div data-id="element-3394">
                    <label className="block text-sm text-secondary mb-2" data-id="element-3395">
                      Province
                    </label>
                    <select className="w-full px-3 py-2 border border-borderColor rounded-lg focus:outline-none focus:ring-2 focus:ring-turquoise/20 focus:border-turquoise text-sm" data-id="element-3396">
                      <option value="" data-id="element-3397">Select Province</option>
                      {canadianProvinces.map(province => <option key={province} value={province} data-id="element-3398">
                          {province}
                        </option>)}
                    </select>
                  </div>
                </div>
              </div>}

            {/* Payment Tab */}
            {activeTab === 'payment' && <div data-id="element-3399">
                <h3 className="text-sm font-semibold text-primary mb-4" data-id="element-3400">
                  Add a New Card
                </h3>

                <div className="space-y-4" data-id="element-3401">
                  <div data-id="element-3402">
                    <label className="block text-sm text-secondary mb-2" data-id="element-3403">
                      Card Number
                    </label>
                    <input type="text" placeholder="1234 1234 1234 1234" className="w-full px-3 py-2 border border-borderColor rounded-lg focus:outline-none focus:ring-2 focus:ring-turquoise/20 focus:border-turquoise text-sm" data-id="element-3404" />
                  </div>

                  <div className="grid grid-cols-2 gap-4" data-id="element-3405">
                    <div data-id="element-3406">
                      <label className="block text-sm text-secondary mb-2" data-id="element-3407">
                        Expiration Date
                      </label>
                      <input type="text" placeholder="MM / YY" className="w-full px-3 py-2 border border-borderColor rounded-lg focus:outline-none focus:ring-2 focus:ring-turquoise/20 focus:border-turquoise text-sm" data-id="element-3408" />
                    </div>
                    <div data-id="element-3409">
                      <label className="block text-sm text-secondary mb-2" data-id="element-3410">
                        Security Code
                      </label>
                      <input type="text" placeholder="CVC" className="w-full px-3 py-2 border border-borderColor rounded-lg focus:outline-none focus:ring-2 focus:ring-turquoise/20 focus:border-turquoise text-sm" data-id="element-3411" />
                    </div>
                  </div>

                  <button className="px-4 py-2 bg-violetBlue text-white rounded-lg hover:bg-violetBlue/90 transition-colors text-sm" data-id="element-3412">
                    Save
                  </button>

                  <div className="flex items-center gap-2 text-xs text-secondary" data-id="element-3413">
                    <Check className="w-4 h-4 text-green-500" data-id="element-3414" />
                    <span data-id="element-3415">Guaranteed safe & secure checkout</span>
                  </div>
                </div>
              </div>}

            {/* Notifications Tab */}
            {activeTab === 'notifications' && <div className="space-y-4" data-id="element-3416">
                <div className="flex items-center justify-between" data-id="element-3417">
                  <div className="flex items-center gap-3" data-id="element-3418">
                    <Bell className="w-5 h-5 text-secondary" data-id="element-3419" />
                    <p className="text-sm font-medium text-primary" data-id="element-3420">Email</p>
                  </div>
                  <button onClick={() => setEmailNotifications(!emailNotifications)} className={`relative w-11 h-6 rounded-full transition-colors ${emailNotifications ? 'bg-turquoise' : 'bg-disabled'}`} data-id="element-3421">
                    <span className={`absolute top-1 left-1 w-4 h-4 bg-white rounded-full transition-transform ${emailNotifications ? 'translate-x-5' : ''}`} data-id="element-3422" />
                  </button>
                </div>

                <div className="flex items-center justify-between" data-id="element-3423">
                  <div className="flex items-center gap-3" data-id="element-3424">
                    <Bell className="w-5 h-5 text-secondary" data-id="element-3425" />
                    <p className="text-sm font-medium text-primary" data-id="element-3426">In-app</p>
                  </div>
                  <button onClick={() => setInAppNotifications(!inAppNotifications)} className={`relative w-11 h-6 rounded-full transition-colors ${inAppNotifications ? 'bg-turquoise' : 'bg-disabled'}`} data-id="element-3427">
                    <span className={`absolute top-1 left-1 w-4 h-4 bg-white rounded-full transition-transform ${inAppNotifications ? 'translate-x-5' : ''}`} data-id="element-3428" />
                  </button>
                </div>
              </div>}

            {/* Subscription Tab */}
            {activeTab === 'subscription' && <div data-id="element-3429">
                <div className="flex items-center justify-between mb-6" data-id="element-3430">
                  <div className="flex items-center gap-3" data-id="element-3431">
                    <Crown className="w-6 h-6 text-turquoise" data-id="element-3432" />
                    <h3 className="text-xl font-bold text-primary" data-id="element-3433">
                      LawVo Unlimited
                    </h3>
                  </div>
                  <button className="px-4 py-2 border border-violetBlue text-violetBlue rounded-lg hover:bg-aliceBlue transition-colors text-sm" data-id="element-3434">
                    Change
                  </button>
                </div>

                <div className="space-y-3 mb-6" data-id="element-3435">
                  {['Unlimited messages with LawVo AI law assistant per month', 'Multilingual conversations', 'Chat attachments', "Unlimited use of LawVo's legal document hub", 'Unlimited simple legal letter drafting up to 6 pages per month', 'Chat history', 'Continue previous conversations', 'Multi-user account', 'Unlimited 24/7 business telephone legal advice', 'Unlimited legal document review up to 6 pages per month'].map((feature, index) => <div key={index} className="flex items-start gap-2" data-id="element-3436">
                      <Check className="w-5 h-5 text-turquoise flex-shrink-0 mt-0.5" data-id="element-3437" />
                      <p className="text-sm text-secondary" data-id="element-3438">{feature}</p>
                    </div>)}
                </div>

                <div className="border-t border-borderColor pt-4" data-id="element-3439">
                  <div className="flex items-center justify-between mb-2" data-id="element-3440">
                    <p className="text-sm font-medium text-secondary" data-id="element-3441">Status</p>
                    <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-xs font-medium" data-id="element-3442">
                      Active
                    </span>
                  </div>
                  <div className="flex items-center justify-between mb-2" data-id="element-3443">
                    <p className="text-sm font-medium text-secondary" data-id="element-3444">
                      Next Billing Date
                    </p>
                    <p className="text-sm text-primary" data-id="element-3445">17 Feb, 2026</p>
                  </div>
                  <div className="flex items-center justify-between" data-id="element-3446">
                    <p className="text-sm font-medium text-secondary" data-id="element-3447">
                      Next Billing Amount
                    </p>
                    <p className="text-lg font-bold text-primary" data-id="element-3448">$0.00</p>
                  </div>
                  <p className="text-xs text-secondary mt-2" data-id="element-3449">
                    Your subscription will automatically renew
                  </p>
                </div>

                <div className="mt-6 flex gap-3" data-id="element-3450">
                  <button className="flex-1 px-4 py-2 bg-violetBlue text-white rounded-lg hover:bg-violetBlue/90 transition-colors text-sm" data-id="element-3451">
                    Renew
                  </button>
                  <button className="flex-1 px-4 py-2 border border-destructive text-destructive rounded-lg hover:bg-destructive/10 transition-colors text-sm" data-id="element-3452">
                    Resend All Invitations
                  </button>
                </div>
              </div>}

            {/* Document Pre-filled Settings Tab */}
            {activeTab === 'document-settings' && <div data-id="element-3453">
                {/* Explanation Banner */}
                <div className="p-4 sm:p-6 bg-turquoise/5 border border-borderColor rounded-lg mb-6" data-id="element-3454">
                  <div className="flex gap-3" data-id="element-3455">
                    <Info className="w-5 h-5 text-turquoise flex-shrink-0 mt-0.5" data-id="element-3456" />
                    <div data-id="element-3457">
                      <h3 className="text-sm font-semibold text-primary mb-1" data-id="element-3458">
                        What are Document Pre-filled Settings?
                      </h3>
                      <p className="text-xs text-secondary leading-relaxed mb-3" data-id="element-3459">
                        Store your personal, business, family, and employee
                        information once, and we'll automatically fill it into
                        your documents. No more repetitive data entry!
                      </p>
                      <div className="space-y-2" data-id="element-3460">
                        <div className="flex items-start gap-2" data-id="element-3461">
                          <Check className="w-4 h-4 text-turquoise flex-shrink-0 mt-0.5" data-id="element-3462" />
                          <p className="text-xs text-secondary" data-id="element-3463">
                            <strong className="text-primary" data-id="element-3464">Save time:</strong>{' '}
                            Fill documents 10x faster with pre-saved information
                          </p>
                        </div>
                        <div className="flex items-start gap-2" data-id="element-3465">
                          <Check className="w-4 h-4 text-turquoise flex-shrink-0 mt-0.5" data-id="element-3466" />
                          <p className="text-xs text-secondary" data-id="element-3467">
                            <strong className="text-primary" data-id="element-3468">
                              Stay accurate:
                            </strong>{' '}
                            Reduce errors by using consistent, verified data
                          </p>
                        </div>
                        <div className="flex items-start gap-2" data-id="element-3469">
                          <Check className="w-4 h-4 text-turquoise flex-shrink-0 mt-0.5" data-id="element-3470" />
                          <p className="text-xs text-secondary" data-id="element-3471">
                            <strong className="text-primary" data-id="element-3472">
                              Manage multiple contexts:
                            </strong>{' '}
                            Store different businesses, family members, and
                            employees
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Personal Information */}
                <div className="mb-6" data-id="element-3473">
                  <div className="flex items-center justify-between mb-4" data-id="element-3474">
                    <h3 className="text-sm font-semibold text-primary" data-id="element-3475">
                      Personal Information
                    </h3>
                    <button onClick={() => setShowPersonalInfoModal(true)} className="text-xs text-turquoise hover:underline flex items-center gap-1" data-id="element-3476">
                      <Edit2 className="w-3 h-3" data-id="element-3477" />
                      Edit
                    </button>
                  </div>
                  <div className="grid grid-cols-2 gap-3 text-xs" data-id="element-3478">
                    <div data-id="element-3479">
                      <p className="text-secondary mb-1" data-id="element-3480">First Name</p>
                      <p className="text-primary font-medium" data-id="element-3481">James</p>
                    </div>
                    <div data-id="element-3482">
                      <p className="text-secondary mb-1" data-id="element-3483">Last Name</p>
                      <p className="text-primary font-medium" data-id="element-3484">Wilson</p>
                    </div>
                    <div data-id="element-3485">
                      <p className="text-secondary mb-1" data-id="element-3486">Email</p>
                      <p className="text-primary font-medium" data-id="element-3487">
                        james.wilson@example.com
                      </p>
                    </div>
                    <div data-id="element-3488">
                      <p className="text-secondary mb-1" data-id="element-3489">Phone Number</p>
                      <p className="text-primary font-medium" data-id="element-3490">
                        +1 (416) 555-0100
                      </p>
                    </div>
                  </div>
                </div>

                {/* My Businesses */}
                <div className="mb-6" data-id="element-3491">
                  <div className="flex items-center justify-between mb-4" data-id="element-3492">
                    <h3 className="text-sm font-semibold text-primary" data-id="element-3493">
                      My Businesses
                    </h3>
                    <button onClick={handleAddBusiness} className="flex items-center gap-1 text-xs text-violetBlue hover:underline" data-id="element-3494">
                      <Plus className="w-3 h-3" data-id="element-3495" />
                      Add Business
                    </button>
                  </div>
                  <div className="space-y-2" data-id="element-3496">
                    {businesses.map(business => <div key={business.id} className="border border-borderColor rounded-lg overflow-hidden" data-id="element-3497">
                        <div className="p-3 bg-aliceBlue/50 flex items-center gap-3" data-id="element-3498">
                          <div className="flex items-center gap-2 flex-1 min-w-0" data-id="element-3499">
                            <Building2 className="w-4 h-4 text-turquoise flex-shrink-0" data-id="element-3500" />
                            <span className="text-sm font-medium text-primary truncate" data-id="element-3501">
                              {business.name}
                            </span>
                          </div>
                          <div className="flex items-center gap-1.5 flex-shrink-0" data-id="element-3502">
                            {business.isDefault && <span className="px-2 py-1 bg-violetBlue/10 text-violetBlue rounded text-xs font-medium flex items-center gap-1 whitespace-nowrap" data-id="element-3503">
                                <Star className="w-3 h-3 fill-violetBlue" data-id="element-3504" />
                                Default
                              </span>}
                            {!business.isDefault && <button onClick={e => {
                        e.stopPropagation();
                        handleSetDefaultBusiness(business.id);
                      }} className="p-1.5 text-turquoise hover:bg-turquoise/10 rounded transition-colors" title="Set as default" data-id="element-3505">
                                <Star className="w-4 h-4" data-id="element-3506" />
                              </button>}
                            <button onClick={e => {
                        e.stopPropagation();
                        handleEditBusiness(business);
                      }} className="p-1.5 text-turquoise hover:bg-turquoise/10 rounded transition-colors" data-id="element-3507">
                              <Edit2 className="w-4 h-4" data-id="element-3508" />
                            </button>
                            <button onClick={e => {
                        e.stopPropagation();
                        handleDeleteBusiness(business.id);
                      }} className="p-1.5 text-destructive hover:bg-destructive/10 rounded transition-colors" data-id="element-3509">
                              <Trash2 className="w-4 h-4" data-id="element-3510" />
                            </button>
                            <button onClick={() => toggleBusinessExpand(business.id)} className="p-1.5 text-secondary hover:bg-secondary/10 rounded transition-colors" data-id="element-3511">
                              {expandedBusinesses.has(business.id) ? <ChevronUp className="w-4 h-4" data-id="element-3512" /> : <ChevronDown className="w-4 h-4" data-id="element-3513" />}
                            </button>
                          </div>
                        </div>
                        {expandedBusinesses.has(business.id) && <div className="p-3 bg-white grid grid-cols-2 gap-2 text-xs" data-id="element-3514">
                            <div data-id="element-3515">
                              <p className="text-secondary mb-1" data-id="element-3516">Address</p>
                              <p className="text-primary" data-id="element-3517">{business.address}</p>
                            </div>
                            <div data-id="element-3518">
                              <p className="text-secondary mb-1" data-id="element-3519">City</p>
                              <p className="text-primary" data-id="element-3520">{business.city}</p>
                            </div>
                            <div data-id="element-3521">
                              <p className="text-secondary mb-1" data-id="element-3522">Province</p>
                              <p className="text-primary" data-id="element-3523">
                                {business.province}
                              </p>
                            </div>
                            <div data-id="element-3524">
                              <p className="text-secondary mb-1" data-id="element-3525">Postal Code</p>
                              <p className="text-primary" data-id="element-3526">
                                {business.postalCode}
                              </p>
                            </div>
                            <div data-id="element-3527">
                              <p className="text-secondary mb-1" data-id="element-3528">HST Number</p>
                              <p className="text-primary" data-id="element-3529">
                                {business.hstNumber}
                              </p>
                            </div>
                            <div data-id="element-3530">
                              <p className="text-secondary mb-1" data-id="element-3531">
                                Registration Number
                              </p>
                              <p className="text-primary" data-id="element-3532">
                                {business.registrationNumber}
                              </p>
                            </div>
                          </div>}
                      </div>)}
                  </div>
                </div>

                {/* Family Members */}
                <div className="mb-6" data-id="element-3533">
                  <div className="flex items-center justify-between mb-4" data-id="element-3534">
                    <h3 className="text-sm font-semibold text-primary" data-id="element-3535">
                      Family Members
                    </h3>
                    <button onClick={handleAddFamily} className="flex items-center gap-1 text-xs text-violetBlue hover:underline" data-id="element-3536">
                      <Plus className="w-3 h-3" data-id="element-3537" />
                      Add Family Member
                    </button>
                  </div>
                  <div className="space-y-2" data-id="element-3538">
                    {familyMembers.map(member => <div key={member.id} className="border border-borderColor rounded-lg overflow-hidden" data-id="element-3539">
                        <div className="p-3 bg-aliceBlue/50 flex items-center gap-3" data-id="element-3540">
                          <div className="flex items-center gap-2 flex-1 min-w-0" data-id="element-3541">
                            <User className="w-4 h-4 text-violetBlue flex-shrink-0" data-id="element-3542" />
                            <span className="text-sm font-medium text-primary truncate" data-id="element-3543">
                              {member.firstName} {member.lastName}
                            </span>
                          </div>
                          <div className="flex items-center gap-1.5 flex-shrink-0" data-id="element-3544">
                            {member.isDefault && <span className="px-2 py-1 bg-violetBlue/10 text-violetBlue rounded text-xs font-medium flex items-center gap-1 whitespace-nowrap" data-id="element-3545">
                                <Star className="w-3 h-3 fill-violetBlue" data-id="element-3546" />
                                Default
                              </span>}
                            {!member.isDefault && <button onClick={e => {
                        e.stopPropagation();
                        handleSetDefaultFamily(member.id);
                      }} className="p-1.5 text-turquoise hover:bg-turquoise/10 rounded transition-colors" title="Set as default" data-id="element-3547">
                                <Star className="w-4 h-4" data-id="element-3548" />
                              </button>}
                            <button onClick={e => {
                        e.stopPropagation();
                        handleEditFamily(member);
                      }} className="p-1.5 text-turquoise hover:bg-turquoise/10 rounded transition-colors" data-id="element-3549">
                              <Edit2 className="w-4 h-4" data-id="element-3550" />
                            </button>
                            <button onClick={e => {
                        e.stopPropagation();
                        handleDeleteFamily(member.id);
                      }} className="p-1.5 text-destructive hover:bg-destructive/10 rounded transition-colors" data-id="element-3551">
                              <Trash2 className="w-4 h-4" data-id="element-3552" />
                            </button>
                            <button onClick={() => toggleFamilyExpand(member.id)} className="p-1.5 text-secondary hover:bg-secondary/10 rounded transition-colors" data-id="element-3553">
                              {expandedFamily.has(member.id) ? <ChevronUp className="w-4 h-4" data-id="element-3554" /> : <ChevronDown className="w-4 h-4" data-id="element-3555" />}
                            </button>
                          </div>
                        </div>
                        {expandedFamily.has(member.id) && <div className="p-3 bg-white grid grid-cols-2 gap-2 text-xs" data-id="element-3556">
                            <div data-id="element-3557">
                              <p className="text-secondary mb-1" data-id="element-3558">First Name</p>
                              <p className="text-primary" data-id="element-3559">{member.firstName}</p>
                            </div>
                            <div data-id="element-3560">
                              <p className="text-secondary mb-1" data-id="element-3561">Last Name</p>
                              <p className="text-primary" data-id="element-3562">{member.lastName}</p>
                            </div>
                            <div data-id="element-3563">
                              <p className="text-secondary mb-1" data-id="element-3564">
                                Relationship
                              </p>
                              <p className="text-primary" data-id="element-3565">
                                {member.relationship}
                              </p>
                            </div>
                          </div>}
                      </div>)}
                  </div>
                </div>

                {/* Employees */}
                <div data-id="element-3566">
                  <div className="flex items-center justify-between mb-4" data-id="element-3567">
                    <h3 className="text-sm font-semibold text-primary" data-id="element-3568">
                      Employees
                    </h3>
                    <button onClick={handleAddEmployee} className="flex items-center gap-1 text-xs text-violetBlue hover:underline" data-id="element-3569">
                      <Plus className="w-3 h-3" data-id="element-3570" />
                      Add Employee
                    </button>
                  </div>
                  <div className="space-y-2" data-id="element-3571">
                    {employees.map(employee => <div key={employee.id} className="border border-borderColor rounded-lg overflow-hidden" data-id="element-3572">
                        <div className="p-3 bg-aliceBlue/50 flex items-center gap-3" data-id="element-3573">
                          <div className="flex items-center gap-2 flex-1 min-w-0" data-id="element-3574">
                            <Briefcase className="w-4 h-4 text-turquoise flex-shrink-0" data-id="element-3575" />
                            <span className="text-sm font-medium text-primary truncate" data-id="element-3576">
                              {employee.firstName} {employee.lastName}
                            </span>
                          </div>
                          <div className="flex items-center gap-1.5 flex-shrink-0" data-id="element-3577">
                            {employee.isDefault && <span className="px-2 py-1 bg-violetBlue/10 text-violetBlue rounded text-xs font-medium flex items-center gap-1 whitespace-nowrap" data-id="element-3578">
                                <Star className="w-3 h-3 fill-violetBlue" data-id="element-3579" />
                                Default
                              </span>}
                            {!employee.isDefault && <button onClick={e => {
                        e.stopPropagation();
                        handleSetDefaultEmployee(employee.id);
                      }} className="p-1.5 text-turquoise hover:bg-turquoise/10 rounded transition-colors" title="Set as default" data-id="element-3580">
                                <Star className="w-4 h-4" data-id="element-3581" />
                              </button>}
                            <button onClick={e => {
                        e.stopPropagation();
                        handleEditEmployee(employee);
                      }} className="p-1.5 text-turquoise hover:bg-turquoise/10 rounded transition-colors" data-id="element-3582">
                              <Edit2 className="w-4 h-4" data-id="element-3583" />
                            </button>
                            <button onClick={e => {
                        e.stopPropagation();
                        handleDeleteEmployee(employee.id);
                      }} className="p-1.5 text-destructive hover:bg-destructive/10 rounded transition-colors" data-id="element-3584">
                              <Trash2 className="w-4 h-4" data-id="element-3585" />
                            </button>
                            <button onClick={() => toggleEmployeeExpand(employee.id)} className="p-1.5 text-secondary hover:bg-secondary/10 rounded transition-colors" data-id="element-3586">
                              {expandedEmployees.has(employee.id) ? <ChevronUp className="w-4 h-4" data-id="element-3587" /> : <ChevronDown className="w-4 h-4" data-id="element-3588" />}
                            </button>
                          </div>
                        </div>
                        {expandedEmployees.has(employee.id) && <div className="p-3 bg-white grid grid-cols-2 gap-2 text-xs" data-id="element-3589">
                            <div data-id="element-3590">
                              <p className="text-secondary mb-1" data-id="element-3591">First Name</p>
                              <p className="text-primary" data-id="element-3592">
                                {employee.firstName}
                              </p>
                            </div>
                            <div data-id="element-3593">
                              <p className="text-secondary mb-1" data-id="element-3594">Last Name</p>
                              <p className="text-primary" data-id="element-3595">
                                {employee.lastName}
                              </p>
                            </div>
                            <div data-id="element-3596">
                              <p className="text-secondary mb-1" data-id="element-3597">Position</p>
                              <p className="text-primary" data-id="element-3598">
                                {employee.position}
                              </p>
                            </div>
                            <div data-id="element-3599">
                              <p className="text-secondary mb-1" data-id="element-3600">Email</p>
                              <p className="text-primary" data-id="element-3601">{employee.email}</p>
                            </div>
                            {employee.phone && <div data-id="element-3602">
                                <p className="text-secondary mb-1" data-id="element-3603">Phone</p>
                                <p className="text-primary" data-id="element-3604">{employee.phone}</p>
                              </div>}
                          </div>}
                      </div>)}
                  </div>
                </div>
              </div>}
          </div>
        </div>
      </main>

      {/* Personal Info Modal */}
      {showPersonalInfoModal && <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-id="element-3605">
          <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto" data-id="element-3606">
            <div className="p-6 border-b border-borderColor flex items-center justify-between sticky top-0 bg-white" data-id="element-3607">
              <h3 className="text-lg font-semibold text-primary" data-id="element-3608">
                Edit Personal Information
              </h3>
              <button onClick={() => setShowPersonalInfoModal(false)} className="p-1 hover:bg-aliceBlue rounded" data-id="element-3609">
                <X className="w-5 h-5 text-secondary" data-id="element-3610" />
              </button>
            </div>

            <div className="p-6 space-y-4" data-id="element-3611">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-3612">
                <div data-id="element-3613">
                  <label className="block text-sm text-secondary mb-2" data-id="element-3614">
                    First Name
                  </label>
                  <input type="text" defaultValue="James" className="w-full px-3 py-2 border border-borderColor rounded-lg focus:outline-none focus:ring-2 focus:ring-turquoise/20 focus:border-turquoise text-sm" data-id="element-3615" />
                </div>
                <div data-id="element-3616">
                  <label className="block text-sm text-secondary mb-2" data-id="element-3617">
                    Last Name
                  </label>
                  <input type="text" defaultValue="Wilson" className="w-full px-3 py-2 border border-borderColor rounded-lg focus:outline-none focus:ring-2 focus:ring-turquoise/20 focus:border-turquoise text-sm" data-id="element-3618" />
                </div>
                <div data-id="element-3619">
                  <label className="block text-sm text-secondary mb-2" data-id="element-3620">
                    Email
                  </label>
                  <input type="email" defaultValue="james.wilson@example.com" className="w-full px-3 py-2 border border-borderColor rounded-lg focus:outline-none focus:ring-2 focus:ring-turquoise/20 focus:border-turquoise text-sm" data-id="element-3621" />
                </div>
                <div data-id="element-3622">
                  <label className="block text-sm text-secondary mb-2" data-id="element-3623">
                    Phone Number
                  </label>
                  <input type="tel" defaultValue="+1 (416) 555-0100" className="w-full px-3 py-2 border border-borderColor rounded-lg focus:outline-none focus:ring-2 focus:ring-turquoise/20 focus:border-turquoise text-sm" data-id="element-3624" />
                </div>
              </div>

              <div className="flex gap-3 pt-4" data-id="element-3625">
                <button onClick={() => setShowPersonalInfoModal(false)} className="flex-1 px-4 py-2 border border-violetBlue text-violetBlue rounded-lg hover:bg-violetBlue/10 transition-colors text-sm" data-id="element-3626">
                  Cancel
                </button>
                <button onClick={() => setShowPersonalInfoModal(false)} className="flex-1 px-4 py-2 bg-violetBlue text-white rounded-lg hover:bg-violetBlue/90 transition-colors text-sm" data-id="element-3627">
                  Save Changes
                </button>
              </div>
            </div>
          </div>
        </div>}

      {/* Business Modal */}
      {showBusinessModal && <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-id="element-3628">
          <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto" data-id="element-3629">
            <div className="p-6 border-b border-borderColor flex items-center justify-between sticky top-0 bg-white" data-id="element-3630">
              <h3 className="text-lg font-semibold text-primary" data-id="element-3631">
                {editingBusiness ? 'Edit Business' : 'Add New Business'}
              </h3>
              <button onClick={() => setShowBusinessModal(false)} className="p-1 hover:bg-aliceBlue rounded" data-id="element-3632">
                <X className="w-5 h-5 text-secondary" data-id="element-3633" />
              </button>
            </div>

            <div className="p-6 space-y-4" data-id="element-3634">
              <div data-id="element-3635">
                <label className="block text-sm text-secondary mb-2" data-id="element-3636">
                  Business Name
                </label>
                <input type="text" placeholder="Maple Leaf Restaurant" defaultValue={editingBusiness?.name} className="w-full px-3 py-2 border border-borderColor rounded-lg focus:outline-none focus:ring-2 focus:ring-turquoise/20 focus:border-turquoise text-sm" data-id="element-3637" />
              </div>

              <div data-id="element-3638">
                <label className="block text-sm text-secondary mb-2" data-id="element-3639">
                  Business Address
                </label>
                <input type="text" placeholder="123 Queen Street West" defaultValue={editingBusiness?.address} className="w-full px-3 py-2 border border-borderColor rounded-lg focus:outline-none focus:ring-2 focus:ring-turquoise/20 focus:border-turquoise text-sm" data-id="element-3640" />
              </div>

              <div className="grid grid-cols-2 gap-4" data-id="element-3641">
                <div data-id="element-3642">
                  <label className="block text-sm text-secondary mb-2" data-id="element-3643">
                    City
                  </label>
                  <input type="text" placeholder="Toronto" defaultValue={editingBusiness?.city} className="w-full px-3 py-2 border border-borderColor rounded-lg focus:outline-none focus:ring-2 focus:ring-turquoise/20 focus:border-turquoise text-sm" data-id="element-3644" />
                </div>

                <div data-id="element-3645">
                  <label className="block text-sm text-secondary mb-2" data-id="element-3646">
                    Province
                  </label>
                  <select defaultValue={editingBusiness?.province} className="w-full px-3 py-2 border border-borderColor rounded-lg focus:outline-none focus:ring-2 focus:ring-turquoise/20 focus:border-turquoise text-sm" data-id="element-3647">
                    <option value="" data-id="element-3648">Select Province</option>
                    {canadianProvinces.map(province => <option key={province} value={province} data-id="element-3649">
                        {province}
                      </option>)}
                  </select>
                </div>
              </div>

              <div data-id="element-3650">
                <label className="block text-sm text-secondary mb-2" data-id="element-3651">
                  Postal Code
                </label>
                <input type="text" placeholder="M5H 2N2" defaultValue={editingBusiness?.postalCode} className="w-full px-3 py-2 border border-borderColor rounded-lg focus:outline-none focus:ring-2 focus:ring-turquoise/20 focus:border-turquoise text-sm" data-id="element-3652" />
              </div>

              <div data-id="element-3653">
                <label className="block text-sm text-secondary mb-2" data-id="element-3654">
                  HST Number
                </label>
                <input type="text" placeholder="123456789RT0001" defaultValue={editingBusiness?.hstNumber} className="w-full px-3 py-2 border border-borderColor rounded-lg focus:outline-none focus:ring-2 focus:ring-turquoise/20 focus:border-turquoise text-sm" data-id="element-3655" />
              </div>

              <div data-id="element-3656">
                <label className="block text-sm text-secondary mb-2" data-id="element-3657">
                  Registration Number
                </label>
                <input type="text" placeholder="ON-12345" defaultValue={editingBusiness?.registrationNumber} className="w-full px-3 py-2 border border-borderColor rounded-lg focus:outline-none focus:ring-2 focus:ring-turquoise/20 focus:border-turquoise text-sm" data-id="element-3658" />
              </div>

              <div className="flex gap-3 pt-4" data-id="element-3659">
                <button onClick={() => setShowBusinessModal(false)} className="flex-1 px-4 py-2 border border-violetBlue text-violetBlue rounded-lg hover:bg-violetBlue/10 transition-colors text-sm" data-id="element-3660">
                  Cancel
                </button>
                <button onClick={() => setShowBusinessModal(false)} className="flex-1 px-4 py-2 bg-violetBlue text-white rounded-lg hover:bg-violetBlue/90 transition-colors text-sm" data-id="element-3661">
                  {editingBusiness ? 'Save Changes' : 'Add Business'}
                </button>
              </div>
            </div>
          </div>
        </div>}

      {/* Family Member Modal */}
      {showFamilyModal && <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-id="element-3662">
          <div className="bg-white rounded-xl max-w-md w-full" data-id="element-3663">
            <div className="p-6 border-b border-borderColor flex items-center justify-between" data-id="element-3664">
              <h3 className="text-lg font-semibold text-primary" data-id="element-3665">
                {editingFamily ? 'Edit Family Member' : 'Add Family Member'}
              </h3>
              <button onClick={() => setShowFamilyModal(false)} className="p-1 hover:bg-aliceBlue rounded" data-id="element-3666">
                <X className="w-5 h-5 text-secondary" data-id="element-3667" />
              </button>
            </div>

            <div className="p-6 space-y-4" data-id="element-3668">
              <div data-id="element-3669">
                <label className="block text-sm text-secondary mb-2" data-id="element-3670">
                  First Name
                </label>
                <input type="text" placeholder="Sarah" defaultValue={editingFamily?.firstName} className="w-full px-3 py-2 border border-borderColor rounded-lg focus:outline-none focus:ring-2 focus:ring-violetBlue/20 focus:border-violetBlue text-sm" data-id="element-3671" />
              </div>

              <div data-id="element-3672">
                <label className="block text-sm text-secondary mb-2" data-id="element-3673">
                  Last Name
                </label>
                <input type="text" placeholder="Johnson" defaultValue={editingFamily?.lastName} className="w-full px-3 py-2 border border-borderColor rounded-lg focus:outline-none focus:ring-2 focus:ring-violetBlue/20 focus:border-violetBlue text-sm" data-id="element-3674" />
              </div>

              <div data-id="element-3675">
                <label className="block text-sm text-secondary mb-2" data-id="element-3676">
                  Relationship
                </label>
                <select defaultValue={editingFamily?.relationship} className="w-full px-3 py-2 border border-borderColor rounded-lg focus:outline-none focus:ring-2 focus:ring-violetBlue/20 focus:border-violetBlue text-sm" data-id="element-3677">
                  <option value="" data-id="element-3678">Select Relationship</option>
                  <option value="Spouse" data-id="element-3679">Spouse</option>
                  <option value="Child" data-id="element-3680">Child</option>
                  <option value="Parent" data-id="element-3681">Parent</option>
                  <option value="Sibling" data-id="element-3682">Sibling</option>
                  <option value="Other" data-id="element-3683">Other</option>
                </select>
              </div>

              <div className="flex gap-3 pt-4" data-id="element-3684">
                <button onClick={() => setShowFamilyModal(false)} className="flex-1 px-4 py-2 border border-violetBlue text-violetBlue rounded-lg hover:bg-violetBlue/10 transition-colors text-sm" data-id="element-3685">
                  Cancel
                </button>
                <button onClick={() => setShowFamilyModal(false)} className="flex-1 px-4 py-2 bg-violetBlue text-white rounded-lg hover:bg-violetBlue/90 transition-colors text-sm" data-id="element-3686">
                  {editingFamily ? 'Save Changes' : 'Add Family Member'}
                </button>
              </div>
            </div>
          </div>
        </div>}

      {/* Employee Modal */}
      {showEmployeeModal && <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-id="element-3687">
          <div className="bg-white rounded-xl max-w-md w-full" data-id="element-3688">
            <div className="p-6 border-b border-borderColor flex items-center justify-between" data-id="element-3689">
              <h3 className="text-lg font-semibold text-primary" data-id="element-3690">
                {editingEmployee ? 'Edit Employee' : 'Add Employee'}
              </h3>
              <button onClick={() => setShowEmployeeModal(false)} className="p-1 hover:bg-aliceBlue rounded" data-id="element-3691">
                <X className="w-5 h-5 text-secondary" data-id="element-3692" />
              </button>
            </div>

            <div className="p-6 space-y-4" data-id="element-3693">
              <div data-id="element-3694">
                <label className="block text-sm text-secondary mb-2" data-id="element-3695">
                  First Name
                </label>
                <input type="text" placeholder="Michael" defaultValue={editingEmployee?.firstName} className="w-full px-3 py-2 border border-borderColor rounded-lg focus:outline-none focus:ring-2 focus:ring-turquoise/20 focus:border-turquoise text-sm" data-id="element-3696" />
              </div>

              <div data-id="element-3697">
                <label className="block text-sm text-secondary mb-2" data-id="element-3698">
                  Last Name
                </label>
                <input type="text" placeholder="Smith" defaultValue={editingEmployee?.lastName} className="w-full px-3 py-2 border border-borderColor rounded-lg focus:outline-none focus:ring-2 focus:ring-turquoise/20 focus:border-turquoise text-sm" data-id="element-3699" />
              </div>

              <div data-id="element-3700">
                <label className="block text-sm text-secondary mb-2" data-id="element-3701">
                  Position
                </label>
                <input type="text" placeholder="Manager" defaultValue={editingEmployee?.position} className="w-full px-3 py-2 border border-borderColor rounded-lg focus:outline-none focus:ring-2 focus:ring-turquoise/20 focus:border-turquoise text-sm" data-id="element-3702" />
              </div>

              <div data-id="element-3703">
                <label className="block text-sm text-secondary mb-2" data-id="element-3704">
                  Email
                </label>
                <input type="email" placeholder="michael.smith@example.com" defaultValue={editingEmployee?.email} className="w-full px-3 py-2 border border-borderColor rounded-lg focus:outline-none focus:ring-2 focus:ring-turquoise/20 focus:border-turquoise text-sm" data-id="element-3705" />
              </div>

              <div data-id="element-3706">
                <label className="block text-sm text-secondary mb-2" data-id="element-3707">
                  Phone (Optional)
                </label>
                <input type="tel" placeholder="+1 (416) 555-0123" defaultValue={editingEmployee?.phone} className="w-full px-3 py-2 border border-borderColor rounded-lg focus:outline-none focus:ring-2 focus:ring-turquoise/20 focus:border-turquoise text-sm" data-id="element-3708" />
              </div>

              <div className="flex gap-3 pt-4" data-id="element-3709">
                <button onClick={() => setShowEmployeeModal(false)} className="flex-1 px-4 py-2 border border-violetBlue text-violetBlue rounded-lg hover:bg-violetBlue/10 transition-colors text-sm" data-id="element-3710">
                  Cancel
                </button>
                <button onClick={() => setShowEmployeeModal(false)} className="flex-1 px-4 py-2 bg-violetBlue text-white rounded-lg hover:bg-violetBlue/90 transition-colors text-sm" data-id="element-3711">
                  {editingEmployee ? 'Save Changes' : 'Add Employee'}
                </button>
              </div>
            </div>
          </div>
        </div>}
    </div>;
}